/**
 * Data transformation utilities for mapping between QuestionDTO and SurveyQuestionData
 *
 * This mapper enables reuse of survey-response components in the question builder
 * by transforming QuestionDTO (builder format) to SurveyQuestionData (response format).
 */

import { QuestionDTO } from '../dtos';
import { SurveyQuestionData } from '../../survey-response/types';

/**
 * Maps a QuestionDTO to SurveyQuestionData format for use in preview components
 *
 * @param dto - The question DTO from the builder
 * @returns SurveyQuestionData compatible with survey-response components
 */
export function mapQuestionDTOToSurveyData(dto: QuestionDTO): SurveyQuestionData {
  return {
    // Direct mappings
    id: dto.id,
    type: dto.type,
    title: dto.title,
    description: dto.description,
    order: dto.order,
    isRequired: dto.isRequired,
    hasCommentField: dto.hasCommentField,
    commentFieldDescription: dto.commentFieldDescription,

    // Options mapping (include color and emoji for preview)
    options: dto.options?.map((option) => ({
      id: option.id,
      label: option.label,
      order: option.order,
      color: option.color,
      emoji: option.emoji,
    })),

    // Range config transformation (flatten labels structure)
    rangeConfig: dto.rangeConfig
      ? {
          min: dto.rangeConfig.min,
          max: dto.rangeConfig.max,
          step: dto.rangeConfig.step ?? 1, // Default step to 1 if not provided
          minLabel: dto.rangeConfig.labels?.min,
          maxLabel: dto.rangeConfig.labels?.max,
          displayType: dto.rangeConfig.displayType,
        }
      : undefined,

    // Validation rules mapping (structure is already compatible)
    validationRules: dto.validationRules
      ? {
          minLength: dto.validationRules.minLength,
          maxLength: dto.validationRules.maxLength,
          minValue: dto.validationRules.minValue,
          maxValue: dto.validationRules.maxValue,
          pattern: dto.validationRules.pattern,
          customMessage: undefined, // QuestionDTO doesn't have customMessage
        }
      : undefined,
  };
}
